import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { MagicWand } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { toast } from "@/hooks/useToast"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import {
  AiRequestMetadataInput,
  referralLetterCopilotQuery,
} from "@/modules/patients/detail/referral/services/referralLetterCopilotQuery"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { Language } from "@/types/__generated__/union-types"

const LanguageMap: Record<Language, string> = {
  ENGLISH: "EN",
  FRENCH: "FR",
  GERMAN: "DE",
  ITALIAN: "IT",
}

type ReferralLetterCopilotButtonProps = {
  language: Language
  progressNoteId: string
  progressNoteAndParentData: ProgressNoteAndParentData[]
  onComplete?: (data: {
    text: string
    fileHash: string
    aiRequestDurationMs: number
    aiAnonymizationRequestDurationMs: number
    aiFeatureId: string
  }) => void
  ref?: React.Ref<HTMLButtonElement>
}

export const ReferralLetterCopilotButton = ({
  language,
  progressNoteId,
  progressNoteAndParentData,
  onComplete,
  ref: forwardedRef,
}: ReferralLetterCopilotButtonProps) => {
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()
  const aiFeatureId = uuidv4()

  const appointmentId = useAppointmentParam()

  const { progressNote, latestProgressNotes } = useMemo(() => {
    const progressNoteAndParent = progressNoteAndParentData.find(
      (p) => p.episodeOfCareProgressNoteData.id === progressNoteId,
    )

    const progressNote = progressNoteAndParent?.episodeOfCareProgressNoteData
    const episodeOfCareId = progressNoteAndParent?.episodeOfCareProgressNoteData.episodeOfCareId

    if (!episodeOfCareId) {
      if (progressNote && progressNote.anamnesis) {
        return { progressNote, latestProgressNotes: [progressNote] }
      } else {
        return { progressNote, latestProgressNotes: [] }
      }
    }

    const latestProgressNotes = progressNoteAndParentData
      .filter(
        (p) =>
          p.episodeOfCareProgressNoteData.episodeOfCareId === episodeOfCareId &&
          !!p.episodeOfCareProgressNoteData.anamnesis,
      )
      .sort((a, b) =>
        b.episodeOfCareProgressNoteData.updatedAt.localeCompare(a.episodeOfCareProgressNoteData.updatedAt),
      )
      .slice(0, 20)
      .map((p) => p.episodeOfCareProgressNoteData)

    return { progressNote, latestProgressNotes }
  }, [progressNoteAndParentData, progressNoteId])

  const getReferralLetterCopilotInput = (progressNote: EpisodeOfCareProgressNoteData): AiRequestMetadataInput => {
    const { pointOfCare } = progressNote
    const selectedSpecialty =
      pointOfCare?.__typename === "ch_PointOfCare"
        ? (pointOfCare.careProviderSpecialty?.specialtyTranslations?.find((s) => s.language === LanguageMap[language])
            ?.name ?? null)
        : null

    return {
      age: patient.age.years,
      appointmentId: appointmentId ?? "",
      aiFeatureId,
      gender: patient.gender,
      targetAgentLanguage: language,
      progressNotes: latestProgressNotes.map((note) => ({
        updatedAt: note.updatedAt,
        anamnesis: note.anamnesis ?? "",
      })),
      specialtyOfPointOfCareForCurrentProgressNote: selectedSpecialty,
    }
  }

  const isReadyForReferralLetterCopilot = ({
    appointmentId,
    progressNote,
    latestProgressNotes,
  }: {
    appointmentId: string | null
    progressNote: EpisodeOfCareProgressNoteData | undefined
    latestProgressNotes: EpisodeOfCareProgressNoteData[]
  }): boolean => {
    if (!appointmentId) {
      return false
    }

    if (!progressNote || (!progressNote.anamnesis && !progressNote.episodeOfCareId)) {
      return false
    }

    if (!latestProgressNotes || latestProgressNotes.every((note) => !note.anamnesis?.trim())) {
      return false
    }

    return true
  }

  const handleReferralLetterCopilotClick = async () => {
    if (
      !progressNote ||
      !isReadyForReferralLetterCopilot({
        appointmentId,
        progressNote,
        latestProgressNotes,
      })
    ) {
      return
    }

    setIsLoading(true)
    sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", true))

    const input = getReferralLetterCopilotInput(progressNote)

    try {
      const response = await referralLetterCopilotQuery(patient.country, input)
      if (response) {
        if (response.referralLetterCopilot.result === "SUCCESS") {
          onComplete?.({
            text: response.referralLetterCopilot.text,
            fileHash: response.referralLetterCopilot.fileHash,
            aiRequestDurationMs: response.referralLetterCopilot.aiRequestDurationMs,
            aiAnonymizationRequestDurationMs: response.referralLetterCopilot.aiAnonymizationRequestDurationMs,
            aiFeatureId: aiFeatureId,
          })
        } else {
          toast({
            description: t(`chat.ai.errors.${response.referralLetterCopilot.result}`),
          })
        }
      }
    } catch {
      toast({
        description: t("patient.referralLetter.ai.errors.fetchReferralLetterCopilotFailed"),
      })
    } finally {
      setIsLoading(false)
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", false))
    }
  }
  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <Button
          className="h-10 w-10 shrink-0 p-1"
          variant="circle"
          isLoading={isLoading}
          disabled={
            !isReadyForReferralLetterCopilot({
              appointmentId,
              progressNote,
              latestProgressNotes,
            })
          }
          ref={forwardedRef}
          onClick={handleReferralLetterCopilotClick}>
          <MagicWand />
        </Button>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="top" align="start">
          {t("patient.referralLetter.referralLetterCopilot")}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}
